import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { api } from '@/lib/api';
import type { CaseStatus } from '..';
import { SuccessPanel } from './shared';

/* ─── Update Status Modal ─── */

interface UpdateStatusModalProps {
    open: boolean;
    caseId: number;
    currentStatus: CaseStatus;
    onClose: () => void;
    onUpdated?: () => void;
}

const ALLOWED_NEXT_STATUSES: CaseStatus[] = ['In Progress', 'Resolved', 'Reopen'];

export function UpdateStatusModal({ open, caseId, currentStatus, onClose, onUpdated }: UpdateStatusModalProps) {
    const [status, setStatus] = useState<CaseStatus>(currentStatus);
    const [note, setNote] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (open) {
            setStatus(currentStatus);
            setError('');
            setDone(false);
        }
    }, [open, currentStatus]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    const handleSubmit = async () => {
        if (status === currentStatus && !note.trim()) return;
        setIsLoading(true);
        setError('');
        try {
            await api.patch(`/api/cases/${caseId}/status`, { status, note: note.trim() || undefined });
            setDone(true);
            setNote('');
            onUpdated?.();
        } catch (err) {
            const message =
                err instanceof Error && err.message ? err.message : 'Failed to update case. Please try again.';
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <>
            <div className="fixed inset-0 z-60 bg-black/40" onClick={onClose} />
            <div className="fixed inset-0 z-61 flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                    {done ? (
                        <SuccessPanel
                            title="Case Updated Successfully!"
                            message={`The case status has been updated to "${status}".`}
                            gradientId="update-grad"
                            onClose={onClose}
                        />
                    ) : (
                        <>
                            <div className="flex items-center justify-between px-6 pt-6 pb-4">
                                <h2 className="text-lg font-bold text-secondary">Update Case Status</h2>
                                <button
                                    type="button"
                                    title="Close"
                                    onClick={onClose}
                                    className="flex h-8 w-8 items-center justify-center rounded-full text-[#555] transition-colors hover:bg-[#f5f5f5]"
                                >
                                    <MdClose size={20} />
                                </button>
                            </div>

                            <div className="px-6">
                                <label
                                    htmlFor="new-status"
                                    className="mb-1 block text-[13px] font-semibold text-secondary"
                                >
                                    New Status
                                </label>
                                <select
                                    id="new-status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value as CaseStatus)}
                                    className="w-full rounded-lg border border-[#D2D2D2] bg-white px-4 py-3 text-[13px] text-secondary outline-none transition-colors focus:border-secondary focus:ring-2 focus:ring-secondary"
                                >
                                    {ALLOWED_NEXT_STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mt-4 px-6">
                                <label
                                    htmlFor="update-note"
                                    className="mb-1 block text-[13px] font-semibold text-secondary"
                                >
                                    Note (optional)
                                </label>
                                <textarea
                                    id="update-note"
                                    rows={5}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Add a note about this update"
                                    className="w-full rounded-lg border border-[#D2D2D2] bg-white px-4 py-3 text-[13px] text-secondary outline-none transition-colors placeholder:text-[#52525B] focus:border-secondary focus:ring-2 focus:ring-secondary"
                                />
                            </div>

                            {error && (
                                <div className="mt-4 px-6">
                                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-600">
                                        {error}
                                    </div>
                                </div>
                            )}

                            <div className="px-6 pb-6 pt-4">
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={isLoading || (status === currentStatus && !note.trim())}
                                    className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[13px] font-bold text-white transition-all hover:bg-secondary-400 disabled:opacity-60"
                                >
                                    {isLoading ? 'Updating…' : 'Update Case'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>,
        document.body,
    );
}
